import Header from '../layouts/Header';
import React, { useState, useRef } from 'react';
import {
  Container, Form, Table, Alert, Spinner, Badge, Card, Button, InputGroup
} from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
// ป้ายชั้นเรียนใช้ตัวเดียวกับหน้าจำหน่ายนักเรียน เพื่อให้ "ม.4/3 (2569)" อ่านได้เหมือนกันทุกหน้า
import { classLabel, islamicClassLabel } from '../registration/studentExitShared';

const DormitoryStudentSearch = () => {
  const [searchKey, setSearchKey] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const inputRef = useRef(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    const key = searchKey.trim();
    if (key.length < 3) {
      setError('กรุณากรอกคำค้นหาอย่างน้อย 3 ตัวอักษร');
      return;
    }

    setError('');
    setLoading(true);
    setSearched(false);
    setResults([]);

    try {
      const res = await axiosInstance.get(`/api/dormitory/students/dorm-search?searchKey=${encodeURIComponent(key)}`);
      if (res.data.success) {
        setResults(res.data.data);
        setSearched(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการค้นหา');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchKey('');
    setResults([]);
    setSearched(false);
    setError('');
    inputRef.current?.focus();
  };

  const genderLabel = (gen) => {
    if (gen === 'm') return <Badge bg="primary">ชาย</Badge>;
    return <Badge bg="danger">หญิง</Badge>;
  };

  const statusLabel = (status) => {
    if (status === '1' || status === '3') return <Badge bg="success">ใช้งาน</Badge>;
    return <Badge bg="secondary">ไม่ใช้งาน</Badge>;
  };

  const allocated = results.filter(r => r.alloc_id);
  const notAllocated = results.filter(r => !r.alloc_id);

  return (
    <React.Fragment>
      <Header />
    <main className="main main-app p-3 p-lg-4">
      <Container fluid className="px-0">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>ค้นหานักเรียนในหอพัก</h2>
        </div>

        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {/* Search Form */}
        <Card className="mb-4">
          <Card.Body>
            <Form onSubmit={handleSearch}>
              <Form.Label><strong>ค้นหานักเรียน</strong></Form.Label>
              <InputGroup>
                <Form.Control
                  ref={inputRef}
                  type="text"
                  value={searchKey}
                  onChange={e => setSearchKey(e.target.value)}
                  placeholder="ค้นหาด้วยเลขประจำตัว, ชื่อ-สกุล, หรือเลขบัตรประชาชน..."
                  autoFocus
                />
                {searchKey && (
                  <Button variant="outline-secondary" onClick={handleClear} type="button">
                    ล้าง
                  </Button>
                )}
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'ค้นหา'}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                กรอกอย่างน้อย 3 ตัวอักษร สามารถค้นหาด้วยเลขประจำตัว, ชื่อ-สกุล หรือเลขบัตรประชาชน
              </Form.Text>
            </Form>
          </Card.Body>
        </Card>

        {/* Results */}
        {searched && !loading && (
          <>
            {results.length === 0 ? (
              <Alert variant="info">ไม่พบนักเรียนที่ตรงกับคำค้นหา "{searchKey}"</Alert>
            ) : (
              <>
                <p className="text-muted mb-3">
                  พบทั้งหมด <strong>{results.length}</strong> รายการ
                  {allocated.length > 0 && (
                    <> — พักอยู่หอ <strong className="text-success">{allocated.length}</strong> คน,
                    ยังไม่ได้รับจัดสรร <strong className="text-secondary">{notAllocated.length}</strong> คน</>
                  )}
                </p>

                {/* Allocated students */}
                {allocated.length > 0 && (
                  <Card className="mb-4 border-success">
                    <Card.Header className="bg-success text-white">
                      <strong>นักเรียนที่พักอยู่ในหอพัก ({allocated.length} คน)</strong>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <Table responsive bordered hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>เลขประจำตัว</th>
                            <th>ชื่อ-สกุล</th>
                            <th>ชั้นสามัญ</th>
                            <th>ชั้นศาสนา</th>
                            <th>เพศ</th>
                            <th>สถานะนักเรียน</th>
                            <th>หมู่หอพัก</th>
                            <th>หอพัก</th>
                            <th>วันที่เข้าพัก</th>
                            <th>หมายเหตุ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allocated.map((row, index) => (
                            <tr key={row.st_id}>
                              <td>{index + 1}</td>
                              <td><code>{row.st_idnumber}</code></td>
                              <td>{row.st_name}</td>
                              <td>{classLabel(row)}</td>
                              <td>{islamicClassLabel(row)}</td>
                              <td>{genderLabel(row.st_gen)}</td>
                              <td>{statusLabel(row.st_status)}</td>
                              <td>
                                <Badge bg={row.village_gender === 'm' ? 'primary' : 'danger'} className="me-1">
                                  {row.village_gender === 'm' ? '👨' : '👩'}
                                </Badge>
                                {row.village_name}
                              </td>
                              <td>
                                <strong>{row.dorm_name}</strong>
                                <small className="d-block text-muted">{row.dorm_code}</small>
                              </td>
                              <td>
                                {row.checkin_date
                                  ? new Date(row.checkin_date).toLocaleDateString('th-TH', {
                                      year: 'numeric', month: 'short', day: 'numeric'
                                    })
                                  : '-'}
                              </td>
                              <td><small>{row.alloc_note || '-'}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}

                {/* Not allocated students */}
                {notAllocated.length > 0 && (
                  <Card className="mb-4 border-secondary">
                    <Card.Header className="bg-light">
                      <strong className="text-secondary">นักเรียนที่ยังไม่ได้รับจัดสรรหอพัก ({notAllocated.length} คน)</strong>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <Table responsive bordered hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>เลขประจำตัว</th>
                            <th>ชื่อ-สกุล</th>
                            <th>ชั้นสามัญ</th>
                            <th>ชั้นศาสนา</th>
                            <th>เพศ</th>
                            <th>สถานะนักเรียน</th>
                          </tr>
                        </thead>
                        <tbody>
                          {notAllocated.map((row, index) => (
                            <tr key={row.st_id} className="text-muted">
                              <td>{index + 1}</td>
                              <td><code>{row.st_idnumber}</code></td>
                              <td>{row.st_name}</td>
                              <td>{classLabel(row)}</td>
                              <td>{islamicClassLabel(row)}</td>
                              <td>{genderLabel(row.st_gen)}</td>
                              <td>{statusLabel(row.st_status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </Container>
    </main>
  </React.Fragment>
  );
};

export default DormitoryStudentSearch;
